import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Pin, CheckCircle2, Clock, CalendarClock, Bell } from "lucide-react";
import { api } from "../../lib/api";

const STATUS = {
  recu: { label: "Reçu", icon: CheckCircle2, chip: "bg-[#EAF5EC] text-[#2E7D46]", dot: "bg-[#2E7D46]" },
  en_cours: { label: "En cours", icon: Clock, chip: "bg-[#E6EEFB] text-[#2456B0]", dot: "bg-[#2456B0]" },
  a_venir: { label: "À venir", icon: CalendarClock, chip: "bg-cream text-ink-muted", dot: "bg-ink-muted" },
};

export default function PaymentPlan() {
  const [plan, setPlan] = useState([]);
  useEffect(() => { api.get("/client/payments").then(({ data }) => setPlan(data)).catch(() => {}); }, []);

  const nextDueId = plan.find((p) => p.status !== "recu")?.id;
  const nextDue = plan.find((p) => p.id === nextDueId);
  const paidCount = plan.filter((p) => p.status === "recu").length;
  const viewStatus = (p) => (p.status === "recu" ? "recu" : p.id === nextDueId ? "en_cours" : "a_venir");
  const fmt = (d) => (!d ? "Date à confirmer" : /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : d);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-6">
      <div>
        <p className="eyebrow">Ton accompagnement</p>
        <h1 className="mt-2 font-sans text-3xl font-medium text-ink sm:text-4xl">Plan de paiement</h1>
      </div>

      <div data-testid="payment-intro-note" className="flex gap-4 rounded-2xl border border-bordeaux-border bg-bordeaux-tint/30 p-5 sm:p-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-bordeaux">
          <Pin size={20} />
        </span>
        <p className="font-light leading-relaxed text-ink-soft">
          J'ai mis en place un plan de paiement pour faciliter le processus. Tu retrouveras ci-dessous le détail des différents
          paiements effectués et ceux à venir. Ceux qui seront validés signifieront que j'ai bien reçu ton virement !
        </p>
      </div>

      {nextDue && (
        <div data-testid="payment-reminder" className="flex items-center gap-3 rounded-2xl border border-[#FDE68A] bg-[#FEF3C7] px-5 py-4 text-[#92400E]">
          <Bell size={18} className="shrink-0" />
          <p className="text-sm">
            <span className="font-semibold">Rappel :</span> prochain versement — {nextDue.label.toLowerCase()}
            {nextDue.date ? <> prévu le <span className="font-semibold">{fmt(nextDue.date)}</span></> : ""}
            {nextDue.amount ? <> · <span className="font-semibold">{nextDue.amount}</span></> : ""}.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between rounded-2xl border border-line bg-white px-5 py-4 shadow-card">
        <span className="text-sm text-ink-soft">Progression des paiements</span>
        <span data-testid="payment-progress" className="text-sm font-semibold text-ink">{paidCount} / {plan.length} reçus</span>
      </div>

      <div className="space-y-3">
        {plan.map((p) => {
          const s = STATUS[viewStatus(p)];
          const Icon = s.icon;
          return (
            <div key={p.id} data-testid={`payment-row-${p.id}`} className="flex items-center gap-4 rounded-2xl border border-line bg-white p-5 shadow-card">
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${s.chip}`}>
                <Icon size={20} />
              </span>
              <div className="flex-1">
                <p className="font-sans text-base font-medium text-ink">{p.label}{p.amount ? ` · ${p.amount}` : ""}</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-sm font-light text-ink-muted">
                  <CalendarClock size={14} /> {fmt(p.date)}
                </p>
              </div>
              <span className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${s.chip}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} /> {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
